import { state } from "../state";
import { triggerSave } from "../persistence";
import type { ScreenContext } from "../app";

/**
 * The first thing a new captain sees (Don's direction): a welcome and a
 * single-page tutorial, read and dismissed before the starting ship choice.
 *
 * One page, not a carousel. A new player has no context yet for anything
 * beyond the loop they are about to be dropped into, so this covers the loop
 * and the three or four rules that are genuinely surprising (you order rather
 * than fly; damage is permanent; the crew eats), and points at the "?" pane
 * for everything else. Detail nobody can act on yet is worse than no detail.
 *
 * Shown once. state.hasSeenWelcome persists in the save, so it does not
 * reappear on every load — and the "?" pane exists precisely so nothing here
 * is lost by dismissing it.
 */
export function renderWelcomeScreen(container: HTMLElement, ctx: ScreenContext): void {
  const wrap = document.createElement("div");
  wrap.className = "welcome-wrap";

  wrap.innerHTML = `
    <h1>Welcome to Starforge</h1>
    <p class="welcome-lede">
      You are a starship captain working the edge of settled space. Not a pilot &mdash; a
      <strong>captain</strong>. You give the orders; your crew flies the ship.
    </p>

    <div class="welcome-grid">
      <section class="welcome-card">
        <h2>The loop</h2>
        <p>
          Take a contract or pick a destination. Fly out, and <strong>mine</strong>,
          <strong>haul</strong>, <strong>fight</strong> or <strong>salvage</strong> your way
          to a payday. Dock, sell, repair, refuel. Spend what you earned on a better ship,
          then go further out where the good stuff is.
        </p>
        <p class="dim">There is no final mission. You are building a fleet and a bank balance.</p>
      </section>

      <section class="welcome-card">
        <h2>Giving orders</h2>
        <p>
          Tap anything to select it &mdash; a rock, a station, a hostile, or empty space.
          Then <strong>Move To</strong> it, or <strong>Orbit</strong> to hold station at range.
          Orbit is how you mine and fight without drifting into what you are shooting at.
        </p>
        <p>
          Your modules sit along the bottom. Arm one and it cycles by itself whenever a valid
          target is in range.
        </p>
      </section>

      <section class="welcome-card">
        <h2>Four things that surprise people</h2>
        <ul>
          <li><strong>Damage is permanent.</strong> Hull and shields do not quietly heal. Repair costs resources, credits, or both.</li>
          <li><strong>Your crew eats.</strong> Provisions burn while time passes. Let them run out and the crew eventually mutinies and sails home without you.</li>
          <li><strong>Ore and cargo are different holds.</strong> Mined resources only go in the Ore Hold. Everything else goes in Cargo.</li>
          <li><strong>Running away is free.</strong> Repairs are not. Not every fight is worth having.</li>
        </ul>
      </section>

      <section class="welcome-card">
        <h2>Your first hour</h2>
        <ol>
          <li>Pick a <strong>Tier 1</strong> ship in a role that appeals &mdash; mining pays most reliably at the start.</li>
          <li>Take a mission or two from the <strong>Mission Board</strong>.</li>
          <li>Fill your hold, dock at a <strong>Station</strong>, and sell.</li>
          <li>Buy fuel and provisions <em>before</em> the next run, not after.</li>
        </ol>
      </section>
    </div>

    <p class="welcome-footer dim">
      Everything else &mdash; a full FAQ, a glossary, every ship and every item &mdash; lives
      behind the <strong>?</strong> button once you are in space. You will not need to remember
      any of this.
    </p>

    <button type="button" class="welcome-continue" data-continue>Choose Your First Ship</button>
  `;

  wrap.querySelector<HTMLButtonElement>("[data-continue]")!.addEventListener("click", () => {
    state.hasSeenWelcome = true;
    // Saved immediately: dismissing this is a real decision, and having it
    // reappear because the flag was still in memory when the tab closed would
    // be exactly the kind of small insult that makes a game feel unfinished.
    triggerSave(ctx);
    ctx.navigate("ships");
  });

  container.appendChild(wrap);
}
